import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { CatalogCategoryFilter, VendorSelect } from 'modules/product';
import { useTheme } from 'context/ThemeContext';
import { selectStylesDark, selectStylesLigth } from 'utils/helpers/structs/selectStyles';
import type useWarrantyFilters from './model/useWarrantyFilters';

interface OffWarrantyFilterFormProps {
    filter: ReturnType<typeof useWarrantyFilters>;
}

interface ObjectFilterOption {
    value: number | 'free';
    label: string;
}

const OffWarrantyFilterForm = ({ filter }: OffWarrantyFilterFormProps) => {
    const { t } = useTranslation(['Warranties', 'Products']);
    const { darkTheme } = useTheme();
    const { draft, setDraft, objects } = filter;
    const objectOptions: ObjectFilterOption[] = [
        { value: 'free', label: t('unassigned') },
        ...objects.map((object) => ({ value: object.ID, label: object.objectName })),
    ];
    const selectedObject = draft.unassigned ? 'free' : draft.objectId;
    const selectTheme = darkTheme ? selectStylesDark : selectStylesLigth;

    return (
        <>
            <CatalogCategoryFilter
                embedded
                kind="product"
                multiple={false}
                filterCategoryIds={draft.categoryId ? [draft.categoryId] : []}
                setFilterCategoryIds={(ids) =>
                    setDraft((previous) => ({ ...previous, categoryId: ids[0] }))
                }
            />
            <Form.Group className="mb-3" controlId="warranty-filter-vendor">
                <Form.Label className="text-muted fs-12 mb-1">{t('fields.vendor')}</Form.Label>
                <VendorSelect
                    inputId="warranty-filter-vendor"
                    value={draft.vendorId}
                    onChange={(option) =>
                        setDraft((previous) => ({ ...previous, vendorId: option?.value }))
                    }
                    required={false}
                    disabled={false}
                    isClearable
                    placeholder={t('Products:selectAllVendors')}
                />
            </Form.Group>
            <Form.Group className="mb-3" controlId="warranty-filter-object">
                <Form.Label className="text-muted fs-12 mb-1">{t('fields.object')}</Form.Label>
                <Select<ObjectFilterOption>
                    inputId="warranty-filter-object"
                    isClearable
                    options={objectOptions}
                    value={objectOptions.find((option) => option.value === selectedObject) || null}
                    placeholder={t('allObjects')}
                    styles={{ ...selectTheme, menuPortal: (base) => ({ ...base, zIndex: 2100 }) }}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                    onChange={(option) =>
                        setDraft((previous) => ({
                            ...previous,
                            unassigned: option?.value === 'free',
                            objectId: typeof option?.value === 'number' ? option.value : undefined,
                        }))
                    }
                />
            </Form.Group>
            <div className="mb-3">
                <label className="form-label text-muted fs-12 mb-1">
                    {t('fields.warrantyUntil')}
                </label>
                {(['all', 'valid', 'expired'] as const).map((term) => (
                    <button
                        key={term}
                        type="button"
                        className="off-filter-item"
                        onClick={() => setDraft((previous) => ({ ...previous, expired: term }))}
                    >
                        <input
                            type="radio"
                            name="warranty-filter-term"
                            className="form-check-input me-2"
                            checked={(draft.expired || 'all') === term}
                            readOnly
                        />
                        {t(term === 'all' ? 'allTerms' : term)}
                    </button>
                ))}
            </div>
            <Form.Check
                type="switch"
                id="warranty-filter-archive"
                className="mb-3"
                label={t('archive')}
                checked={Boolean(draft.archived)}
                onChange={(event) =>
                    setDraft((previous) => ({ ...previous, archived: event.target.checked }))
                }
            />
        </>
    );
};

export default OffWarrantyFilterForm;
